import classNames from "classnames";
import { DotsCheckbox } from "dots-ui/dotsCheckbox";
import { DotsToastMessage } from "dots-ui/dotsToast";
import { FormikProps } from "formik";
import { useContext, useEffect } from "react";
import { PasswordPolicySettingsViewModel } from "../../generated/data-contracts";
import { IExtendedChangePasswordViewModel, LoginType } from "../../models";
import { FormikUtils } from "../../utils";
import { AvisSaveButton } from "../shared/components/buttons";
import { EIdActionPicker } from "../shared/components/eId/eIdActionPicker";
import { AvisInputText } from "../shared/components/inputs";
import { AvisContext } from "../shared/context/AvisContext";
import { PasswordPoliciesUl } from "./PasswordPoliciesUl";

interface IChangePasswordFormProps {
  bag: FormikProps<IExtendedChangePasswordViewModel>;
  loading: boolean;
  canUseEId: boolean;
  forgotPassword: boolean;
  setForgotPassword: (value: boolean) => void;
  policies: PasswordPolicySettingsViewModel | null;
  showModal: () => void;
}

export function ChangePasswordForm(props: Readonly<IChangePasswordFormProps>) {
  const {
    bag,
    loading,
    forgotPassword,
    setForgotPassword,
    canUseEId,
    policies,
    showModal,
  } = props;
  const { res } = useContext(AvisContext);
  useEffect(() => {
    if (forgotPassword) {
      bag.validateForm();
    }

    //todo - is there a workaround to not include bag as dependency and validate form on cbx change?
    //disabling eslint error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forgotPassword]);

  const onSubmitPrompt = async (loginType?: LoginType) => {
    if (!loginType) loginType = "regular";

    bag.setFieldValue("loginType", loginType);
    const errors = await bag.validateForm();
    if (Object.keys(errors).length > 0) return;

    showModal();
  };

  const renderPoliciesInformation = (
    policiesSettings: PasswordPolicySettingsViewModel | null,
  ) => {
    if (policiesSettings === null) return <></>;

    const passwordPoliciesContainer = classNames("password-policies-container");
    const msg = (
      <div>
        <p>{res("user_ChangePassword_RequirementsTitle")}</p>
        <PasswordPoliciesUl policies={policiesSettings} />
      </div>
    );
    return (
      <div className={passwordPoliciesContainer}>
        <DotsToastMessage
          message={{ content: msg, severity: "info", display: "inline" }}
        />
      </div>
    );
  };

  return (
    <>
      {renderPoliciesInformation(policies)}
      <AvisInputText<IExtendedChangePasswordViewModel>
        placeholder={res("user_ChangePassword_OldPassword")}
        bag={bag}
        bagField="oldPassword"
        name="oldPassword"
        disabled={loading || forgotPassword}
        type="password"
        onKeyDown={(e) => FormikUtils.handleEnterKeyPress(e, onSubmitPrompt)}
        reserveErrorMessageSpace={true}
      />
      {canUseEId && (
        <DotsCheckbox
          checkboxLabel={res("user_ChangePassword_ForgotPassword")}
          checked={forgotPassword}
          onChange={() => {
            bag.setFieldValue("oldPassword", "");
            setForgotPassword(!forgotPassword);
          }}
          disabled={loading}
        />
      )}
      <AvisInputText<IExtendedChangePasswordViewModel>
        placeholder={res("user_ChangePassword_Password1")}
        bag={bag}
        bagField="password1"
        disabled={loading}
        type="password"
        onKeyDown={(e) => FormikUtils.handleEnterKeyPress(e, onSubmitPrompt)}
        reserveErrorMessageSpace={true}
      />
      <AvisInputText<IExtendedChangePasswordViewModel>
        placeholder={res("user_ChangePassword_Password2")}
        bag={bag}
        bagField="password2"
        disabled={loading}
        type="password"
        onKeyDown={(e) => FormikUtils.handleEnterKeyPress(e, onSubmitPrompt)}
        reserveErrorMessageSpace={true}
      />
      {!forgotPassword && (
        <AvisSaveButton onClick={() => onSubmitPrompt()} disabled={loading} />
      )}

      {canUseEId && forgotPassword && (
        <EIdActionPicker
          isValid={bag.isValid}
          action={onSubmitPrompt}
          loading={loading}
        />
      )}
    </>
  );
}
